import { createSlice, isAnyOf } from '@reduxjs/toolkit'

import { SelectInterface } from '../../interfaces/InterfacesLocal'
import {
  AreasInterface,
  AttributeInterface,
  FixedCostCategoriesInterface,
  MeasuresNomenclator,
  OriginDestinyInterface,
  PaymentMethodsInterface,
  PersonCategory,
  PostInterface,
  ProductCategoriesInterface,
  Regions,
  RolesInterface,
  SalesCategories,
  UserInterface,
} from '../../interfaces/ServerInterfaces'
import { changeBusiness, initSystem } from '../actions/globals'

interface elToqueRateInterface {
  tasas: {
    MLC: number
    ECU: number
    USD: number
    USDT_TRC20: number
    BTC: number
  }
  date: string
  hour: number
  minutes: number
  seconds: number
}

interface InitialData {
  areas: AreasInterface[]
  finderAreas: AreasInterface[]
  measures: MeasuresNomenclator[]
  productCategories: ProductCategoriesInterface[]
  salesCategories: SalesCategories[]
  originDestinys: OriginDestinyInterface[]
  businessUsers: SelectInterface[]
  roles: RolesInterface[]
  product_attributes: AttributeInterface[]
  paymentWays: PaymentMethodsInterface[]
  personCategories: PersonCategory[]
  personPosts: PostInterface[]
  elToqueRates: elToqueRateInterface | null
  titlesForExport: string[]
  fixedCostCategories: FixedCostCategoriesInterface[]
  deliverymans: {
    avatar: string | null
    businessId: number
    displayName: string
    email: string
    id: number
    isActive: boolean
    lastLogin: string
    roles: {
      name: string
      code: string
    }[]
    username: string
  }[]
  users: UserInterface[] | null
  shippingRegions: Regions[]
}

const initialState: InitialData = {
  areas: [],
  finderAreas: [],
  measures: [],
  productCategories: [],
  originDestinys: [],
  salesCategories: [],
  businessUsers: [],
  roles: [],
  product_attributes: [],
  paymentWays: [],
  personCategories: [],
  personPosts: [],
  elToqueRates: null,
  titlesForExport: [''],
  fixedCostCategories: [],
  deliverymans: [],
  users: null,
  shippingRegions: [],
}

const nomenclatorSlice = createSlice({
  initialState,
  name: 'nomenclators',
  reducers: {
    //areas---------------------------------------------------------------------------------------
    setAreas: (state, action) => (state = { ...state, areas: action.payload }),
    updateAreas: (state, action) =>
      (state = { ...state, areas: [action.payload, ...state.areas] }),
    updateCurrentArea: (state, action) => {
      const element = state.areas.findIndex(
        (item) => item.id === action.payload.id
      )
      state.areas.splice(element, 1, action.payload)
    },
    updateAreaAfterDelete: (state, action) => {
      state.areas.splice(
        state.areas.findIndex((item) => item.id === Number(action.payload)),
        1
      )
    },
    //finderAreas---------------------------------------------------------------------------------------
    setFinderAreas: (state, action) =>
      (state = { ...state, finderAreas: action.payload }),
    updateFinderAreas: (state, action) =>
      (state = {
        ...state,
        finderAreas: [action.payload, ...state.finderAreas],
      }),
    updateCurrentFinderArea: (state, action) => {
      const element = state.finderAreas.findIndex(
        (item) => item.id === action.payload.id
      )
      state.finderAreas.splice(element, 1, action.payload)
    },
    updateFinderAreaAfterDelete: (state, action) => {
      state.finderAreas.splice(
        state.finderAreas.findIndex(
          (item) => item.id === Number(action.payload)
        ),
        1
      )
    },
    //measure------------------------------------------------------------------------------------------
    setMeasures: (state, action) =>
      (state = { ...state, measures: action.payload }),
    //product categories ------------------------------------------------------------------
    setProductCategories: (state, action) =>
      (state = { ...state, productCategories: action.payload }),
    addProductCategory: (state, action) =>
      (state = {
        ...state,
        productCategories: [...state.productCategories, action.payload],
      }),
    updateProductCategory: (state, action) => {
      const element = state.productCategories.findIndex(
        (item) => item.id === action.payload.id
      )
      state.productCategories.splice(element, 1, action.payload)
    },
    updateProductCategoryAfterDelete: (state, action) => {
      state.productCategories.splice(
        state.productCategories.findIndex(
          (item) => item.id === Number(action.payload)
        ),
        1
      )
    },
    //sales categories---------------------------------------------------------------------
    setSalesCategories: (state, action) =>
      (state = { ...state, salesCategories: action.payload }),
    addSalesCategory: (state, action) =>
      (state = {
        ...state,
        salesCategories: [...state.salesCategories, action.payload],
      }),
    updateSalesCategory: (state, action) => {
      const element = state.salesCategories.findIndex(
        (item) => item.id === action.payload.id
      )
      state.salesCategories.splice(element, 1, action.payload)
    },
    updateSalesCategoryAfterDelete: (state, action) => {
      state.salesCategories.splice(
        state.salesCategories.findIndex(
          (item) => item.id === Number(action.payload)
        ),
        1
      )
    },
    //User by business---------------------------------------------------------------------------
    setBusinessUsers: (state, action) =>
      (state = { ...state, businessUsers: action.payload }),
    setUsers: (state, action) => (state = { ...state, users: action.payload }),
    //Origind/Destiny
    setOriginDestinys: (state, action) =>
      (state = { ...state, originDestinys: action.payload }),
    //roles--------------------------------------------------------------------------------------------
    setRoles: (state, action) => (state = { ...state, roles: action.payload }),
    //Person Categories---------------------------------------------------------------------------
    setPersonCategories: (state, action) =>
      (state = { ...state, personCategories: action.payload }),
    //Person Posts---------------------------------------------------------------------------
    setPersonPost: (state, action) =>
      (state = { ...state, personPosts: action.payload }),
    //ElToque Rates---------------------------------------------------------------------------
    setElToqueRates: (state, action) =>
      (state = { ...state, elToqueRates: action.payload }),
    setTitlesForExport: (state, action) => ({
      ...state,
      titlesForExport: action.payload,
    }),
    //Fixed Costs Categories ----------------------------------------------------------
    addFixedCosts: (state, action) =>
      (state = {
        ...state,
        fixedCostCategories: [...state.fixedCostCategories, action.payload],
      }),
    updateFixedCosts: (state, action) => {
      const element = state.fixedCostCategories.findIndex(
        (item) => item.id === action.payload.id
      )
      state.fixedCostCategories.splice(element, 1, action.payload)
    },
    updateFixedCostsAfterDelete: (state, action) => {
      state.fixedCostCategories.splice(
        state.fixedCostCategories.findIndex(
          (item) => item.id === Number(action.payload)
        ),
        1
      )
    },
    setFixedCosts: (state, action) => {
      state.fixedCostCategories = action.payload
    },
    setDeliverymans: (state, action) =>
      (state = { ...state, deliverymans: action.payload }),
  },
  extraReducers: (builder) =>
    builder.addMatcher(
      isAnyOf(initSystem, changeBusiness.fulfilled),
      (state, action) => {
        return {
          fixedCostCategories: action.payload.fixedCostCategories,
          areas: action.payload.areas,
          finderAreas: action.payload.finderAreas,
          measures: action.payload.measures,
          originDestinys: action.payload.originDestinys,
          productCategories: action.payload.productCategories,
          salesCategories: action.payload.salesCategories,
          businessUsers: action.payload.businessUsers,
          roles: action.payload.roles,
          product_attributes: action.payload.product_attributes,
          paymentWays: action.payload.paymentWays,
          personCategories: action.payload.personCategories,
          personPosts: action.payload.personPosts,
          elToqueRates: action.payload.elToqueRates,
          titlesForExport: action.payload.titlesForExport,
          deliverymans: action.payload.deliverymans,
          users: action.payload.users,
          shippingRegions: action.payload.shippingRegions,
        }
      }
    ),
})

export const {
  addProductCategory,
  addSalesCategory,
  setBusinessUsers,
  setMeasures,
  setProductCategories,
  setRoles,
  setSalesCategories,
  setElToqueRates,
  updateAreaAfterDelete,
  updateFinderAreaAfterDelete,
  setAreas,
  updateAreas,
  updateCurrentArea,
  setFinderAreas,
  updateFinderAreas,
  updateCurrentFinderArea,
  updateProductCategory,
  updateProductCategoryAfterDelete,
  updateSalesCategory,
  updateSalesCategoryAfterDelete,
  setPersonCategories,
  setOriginDestinys,
  setPersonPost,
  setTitlesForExport,
  addFixedCosts,
  updateFixedCosts,
  updateFixedCostsAfterDelete,
  setFixedCosts,
  setDeliverymans,
  setUsers,
} = nomenclatorSlice.actions
export default nomenclatorSlice.reducer
